import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, Plus, Trash2 } from "lucide-react";
import { api, ApiError, type AuthConfig, type AuthMode } from "@/lib/api";
import {
  Badge,
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  Empty,
  ErrorNote,
  Field,
  Input,
  Notice,
  Select,
  Spinner,
  Table,
  Td,
  Th,
  Toolbar,
} from "@/components/ui";

/**
 * Wie sich die Leute einer Firma im Portal anmelden.
 *
 * Zwei Wege: das eigene Passwort im Core, oder der Identitätsanbieter des
 * Kunden (Entra, Google, ein Keycloak). Die Seite ist für den globalen
 * Administrator gedacht — die Routen dahinter lassen niemand anderen zu, weil
 * der Issuer eine Adresse ist, die der Server anschließend selbst abruft.
 *
 * Das Client-Secret taucht hier nirgends auf. Es liegt als Referenz in der
 * Umgebung des Servers, und die Seite sagt nur, ob die Referenz auflösbar ist.
 */
export function CompanyAuth({ companyId }: { companyId: number }) {
  const qc = useQueryClient();
  const key = ["company", companyId, "auth"];

  const { data, isLoading, error } = useQuery({
    queryKey: key,
    queryFn: () => api<AuthConfig>(`/api/admin/companies/${companyId}/auth`),
  });

  const refresh = () => qc.invalidateQueries({ queryKey: key });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorNote>{(error as Error).message}</ErrorNote>;
  if (!data) return null;

  return (
    <div className="flex flex-col gap-4">
      <ProviderCard companyId={companyId} config={data} onSaved={refresh} />
      {data.mode === "oidc" && <RedirectCard config={data} />}
      <DomainsCard companyId={companyId} config={data} onChanged={refresh} />
      <InviteCard companyId={companyId} config={data} onSent={refresh} />
    </div>
  );
}

// --- the provider ---------------------------------------------------------

type Form = {
  mode: AuthMode;
  issuer: string;
  clientId: string;
  credentialsRef: string;
  scopes: string;
  emailClaims: string;
  requireVerifiedEmail: boolean;
  allowJitProvisioning: boolean;
  displayName: string;
  extraParams: { key: string; value: string }[];
  enabled: boolean;
};

function formOf(c: AuthConfig): Form {
  return {
    mode: c.mode,
    issuer: c.issuer ?? "",
    clientId: c.clientId ?? "",
    credentialsRef: c.credentialsRef ?? "",
    scopes: c.scopes ?? "",
    emailClaims: c.emailClaims ?? "",
    requireVerifiedEmail: c.requireVerifiedEmail,
    allowJitProvisioning: c.allowJitProvisioning,
    displayName: c.displayName ?? "",
    extraParams: Object.entries(c.extraParams ?? {}).map(([key, value]) => ({ key, value })),
    // Eine Firma, die noch nie einen Anbieter hatte, bekommt "aktiv"
    // vorbelegt; sonst wäre der erste Speichervorgang eine Konfiguration, die
    // niemanden hereinlässt.
    enabled: c.issuer ? c.enabled : true,
  };
}

/**
 * Vorlagen für die Anbieter, die in der Praxis vorkommen. Sie füllen nur
 * die Felder, die je Anbieter gleich sind; Mandant und Client bleiben Sache
 * des Kunden.
 */
const presets: {
  id: string;
  label: string;
  issuer: string;
  displayName: string;
  emailClaims: string;
  extraParams: { key: string; value: string }[];
  hint: string;
}[] = [
  {
    id: "entra",
    label: "Microsoft Entra ID (Microsoft 365)",
    issuer: "https://login.microsoftonline.com/<Mandanten-ID>/v2.0",
    displayName: "Mit Microsoft 365 anmelden",
    emailClaims: "email preferred_username upn",
    extraParams: [{ key: "prompt", value: "select_account" }],
    hint:
      "Die Mandanten-ID steht im Entra-Portal unter „Übersicht“. Entra sendet „email“ nur, wenn der Kunde den optionalen Claim eingerichtet hat, sonst preferred_username.",
  },
  {
    id: "google",
    label: "Google Workspace",
    issuer: "https://accounts.google.com",
    displayName: "Mit Google anmelden",
    emailClaims: "",
    extraParams: [{ key: "hd", value: "<kunde.at>" }],
    hint: "„hd“ beschränkt die Kontoauswahl bei Google auf die Domäne des Kunden.",
  },
  {
    id: "keycloak",
    label: "Keycloak",
    issuer: "https://<keycloak.kunde.at>/realms/<realm>",
    displayName: "Anmelden",
    emailClaims: "",
    extraParams: [],
    hint: "Der Issuer ist die Realm-Adresse ohne abschließenden Schrägstrich.",
  },
];

function ProviderCard({
  companyId,
  config,
  onSaved,
}: {
  companyId: number;
  config: AuthConfig;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<Form>(() => formOf(config));
  const [inviteAll, setInviteAll] = useState(false);
  const [preset, setPreset] = useState("");
  useEffect(() => {
    setForm(formOf(config));
    setInviteAll(false);
  }, [config]);

  const save = useMutation({
    mutationFn: () =>
      api<AuthConfig>(`/api/admin/companies/${companyId}/auth`, {
        method: "PUT",
        body: {
          companyId,
          mode: form.mode,
          issuer: form.issuer.trim(),
          clientId: form.clientId.trim(),
          credentialsRef: form.credentialsRef.trim(),
          scopes: form.scopes.trim(),
          emailClaims: form.emailClaims.trim(),
          requireVerifiedEmail: form.requireVerifiedEmail,
          allowJitProvisioning: form.allowJitProvisioning,
          displayName: form.displayName.trim(),
          extraParams: Object.fromEntries(
            form.extraParams
              .filter((p) => p.key.trim())
              .map((p) => [p.key.trim(), p.value]),
          ),
          enabled: form.enabled,
          domains: [],
          inviteAll,
        },
      }),
    onSuccess: onSaved,
  });

  const fields = save.error instanceof ApiError ? (save.error.fields ?? {}) : {};
  const oidc = form.mode === "oidc";
  const leavingProvider = config.mode === "oidc" && form.mode === "local";
  const sessionsEnd =
    config.mode !== form.mode ||
    (oidc &&
      ((config.issuer ?? "") !== form.issuer.trim() ||
        (config.clientId ?? "") !== form.clientId.trim()));

  function applyPreset(id: string) {
    setPreset(id);
    const p = presets.find((x) => x.id === id);
    if (!p) return;
    setForm({
      ...form,
      issuer: form.issuer || p.issuer,
      displayName: form.displayName || p.displayName,
      emailClaims: form.emailClaims || p.emailClaims,
      extraParams: form.extraParams.length ? form.extraParams : p.extraParams,
    });
  }

  return (
    <Card className="p-4">
      <h3 className="mb-3 text-sm font-medium">Anmeldung im Portal</h3>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field
          label="Anmeldeverfahren"
          error={fields.mode}
          hint={
            oidc
              ? "Die Leute melden sich beim Anbieter des Kunden an; der Core prüft dessen Antwort. Wer im Portal auf „Weiter“ klickt, wird dorthin geschickt."
              : "Der Core prüft das Passwort selbst. Ein neues Konto wird per Einladungsmail freigeschaltet."
          }
        >
          <Select
            value={form.mode}
            onChange={(e) => setForm({ ...form, mode: e.target.value as AuthMode })}
          >
            <option value="local">Passwort im Mobilboxmanager</option>
            <option value="oidc">Identitätsanbieter des Kunden (OpenID Connect)</option>
          </Select>
        </Field>

        {oidc && (
          <Field label="Vorlage" hint={presets.find((p) => p.id === preset)?.hint ?? "Füllt leere Felder mit dem, was bei diesem Anbieter immer gleich ist."}>
            <Select value={preset} onChange={(e) => applyPreset(e.target.value)}>
              <option value="">— keine —</option>
              {presets.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </Select>
          </Field>
        )}
      </div>

      {oidc && (
        <>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <Field
              label="Issuer"
              error={fields.issuer}
              hint="Genau so, wie er im Token des Anbieters steht (iss). https, ohne Schrägstrich am Ende. Der Server ruft daraus die Discovery ab."
            >
              <Input
                value={form.issuer}
                placeholder="https://login.microsoftonline.com/…/v2.0"
                onChange={(e) => setForm({ ...form, issuer: e.target.value })}
                spellCheck={false}
              />
            </Field>
            <Field label="Client-ID" error={fields.clientId} hint="Die Anwendungs-ID, die der Kunde für den Mobilboxmanager angelegt hat.">
              <Input
                value={form.clientId}
                onChange={(e) => setForm({ ...form, clientId: e.target.value })}
                spellCheck={false}
              />
            </Field>

            <Field
              label="Client-Secret (Referenz)"
              error={fields.credentialsRef}
              hint="Nie das Secret selbst: „env:MBM_OIDC_KUNDE“ oder „file:/run/secrets/…“. Der Betrieb legt den Wert in die Umgebung des Servers. Leer für einen öffentlichen Client (PKCE ohne Secret)."
            >
              <div className="flex items-center gap-2">
                <Input
                  value={form.credentialsRef}
                  placeholder="env:MBM_OIDC_KUNDE"
                  onChange={(e) => setForm({ ...form, credentialsRef: e.target.value })}
                  spellCheck={false}
                />
                <SecretState config={config} current={form.credentialsRef} />
              </div>
            </Field>
            <Field label="Beschriftung des Knopfs" hint="Was im Portal auf dem Knopf steht, z. B. „Mit Microsoft 365 anmelden“.">
              <Input
                value={form.displayName}
                onChange={(e) => setForm({ ...form, displayName: e.target.value })}
              />
            </Field>

            <Field label="Scopes" error={fields.scopes} hint="Leer bedeutet „openid email profile“. „openid“ muss dabei sein.">
              <Input
                value={form.scopes}
                placeholder="openid email profile"
                onChange={(e) => setForm({ ...form, scopes: e.target.value })}
                spellCheck={false}
              />
            </Field>
            <Field
              label="E-Mail-Claims"
              error={fields.emailClaims}
              hint="In welchem Claim die Adresse steht, in dieser Reihenfolge. Leer: email, upn, preferred_username, unique_name, mail. Es wird nie in anderen Claims gesucht."
            >
              <Input
                value={form.emailClaims}
                placeholder="email upn preferred_username"
                onChange={(e) => setForm({ ...form, emailClaims: e.target.value })}
                spellCheck={false}
              />
            </Field>
          </div>

          <div className="mt-3 flex flex-col gap-1">
            <Checkbox
              label="Konfiguration aktiv"
              hint="Ohne Haken bleibt alles gespeichert, aber niemand kann sich über den Anbieter anmelden."
              checked={form.enabled}
              onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
            />
            <Checkbox
              label="Nur bestätigte E-Mail-Adressen"
              hint="Verlangt email_verified=true vom Anbieter. Entra lässt den Claim oft weg — dann kommt niemand herein."
              checked={form.requireVerifiedEmail}
              onChange={(e) => setForm({ ...form, requireVerifiedEmail: e.target.checked })}
            />
            <Checkbox
              label="Konten beim ersten Anmelden anlegen"
              hint="Wer beim Anbieter bekannt ist, aber hier kein Konto hat, bekommt eines — ohne Rufnummer, nicht abrechenbar, bis die Administration es vervollständigt. Ohne Haken: „Ihr Zugang ist noch nicht eingerichtet“."
              checked={form.allowJitProvisioning}
              onChange={(e) => setForm({ ...form, allowJitProvisioning: e.target.checked })}
            />
          </div>

          <ExtraParams
            value={form.extraParams}
            error={fields.extraParams}
            onChange={(extraParams) => setForm({ ...form, extraParams })}
          />
        </>
      )}

      {leavingProvider && (
        <div className="mt-4 flex flex-col gap-2">
          <Notice tone="warning">
            {config.passwordlessCount > 0 ? (
              <>
                <strong>{config.passwordlessCount}</strong>{" "}
                {config.passwordlessCount === 1 ? "Person hat" : "Personen haben"} kein Passwort und
                könnten sich nach der Umstellung nicht mehr anmelden. Die Umstellung geht nur,
                wenn sie eine Einladung bekommen.
              </>
            ) : (
              <>Alle Personen dieser Firma haben ein Passwort; niemand wird ausgesperrt.</>
            )}
          </Notice>
          {config.passwordlessCount > 0 && (
            <Checkbox
              label="Allen Personen ohne Passwort eine Einladung schicken"
              hint="Der Link ist zwei Wochen gültig und setzt das erste Passwort."
              checked={inviteAll}
              onChange={(e) => setInviteAll(e.target.checked)}
            />
          )}
        </div>
      )}

      {sessionsEnd && !leavingProvider && (
        <div className="mt-4">
          <Notice tone="warning">
            Diese Änderung beendet alle Portal-Sitzungen der Firma. Wer gerade angemeldet ist,
            muss sich neu anmelden — sonst behielte jemand, den der Kunde aus seinem Anbieter
            entfernt hat, bis zu dreißig Tage Zugang.
          </Notice>
        </div>
      )}

      {save.error && !Object.keys(fields).length && (
        <div className="mt-3">
          <ErrorNote>{(save.error as Error).message}</ErrorNote>
        </div>
      )}
      {save.isSuccess && (
        <div className="mt-3">
          <Notice tone="success">Gespeichert.</Notice>
        </div>
      )}

      <div className="mt-4">
        <Toolbar>
          <Button
            variant="primary"
            onClick={() => save.mutate()}
            disabled={save.isPending || (leavingProvider && config.passwordlessCount > 0 && !inviteAll)}
          >
            {save.isPending ? "Speichert…" : "Speichern"}
          </Button>
        </Toolbar>
      </div>
    </Card>
  );
}

/** Ob die Referenz aufs Secret in der Umgebung des Servers ankommt. */
function SecretState({ config, current }: { config: AuthConfig; current: string }) {
  if (!current.trim()) return null;
  if (current.trim() !== (config.credentialsRef ?? "")) {
    return <Badge tone="neutral">ungeprüft</Badge>;
  }
  if (config.secretResolves === undefined) return null;
  return config.secretResolves ? (
    <Badge tone="success">auflösbar</Badge>
  ) : (
    <Badge tone="danger">nicht auflösbar</Badge>
  );
}

/**
 * Zusätzliche Parameter der Autorisierungsanfrage. Die Protokollnamen
 * (client_id, redirect_uri, scope, state, nonce …) weist der Server zurück.
 */
function ExtraParams({
  value,
  error,
  onChange,
}: {
  value: { key: string; value: string }[];
  error?: string;
  onChange: (v: { key: string; value: string }[]) => void;
}) {
  function set(i: number, patch: Partial<{ key: string; value: string }>) {
    onChange(value.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  }
  return (
    <div className="mt-4">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs font-medium muted">Zusätzliche Parameter</span>
        <Button size="sm" variant="ghost" onClick={() => onChange([...value, { key: "", value: "" }])}>
          <Plus className="size-3.5" aria-hidden /> Parameter
        </Button>
      </div>
      {value.length === 0 ? (
        <p className="text-xs muted">
          Zum Beispiel prompt=select_account oder domain_hint=kunde.at. Sie werden unverändert an
          den Anbieter weitergegeben.
        </p>
      ) : (
        <div className="flex flex-col gap-1.5">
          {value.map((p, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input
                value={p.key}
                placeholder="Name"
                onChange={(e) => set(i, { key: e.target.value })}
                spellCheck={false}
                className="sm:max-w-56"
              />
              <span className="muted">=</span>
              <Input
                value={p.value}
                placeholder="Wert"
                onChange={(e) => set(i, { value: e.target.value })}
                spellCheck={false}
              />
              <Button
                size="sm"
                variant="ghost"
                aria-label="Parameter entfernen"
                title="Entfernen"
                onClick={() => onChange(value.filter((_, j) => j !== i))}
              >
                <Trash2 className="size-3.5" aria-hidden />
              </Button>
            </div>
          ))}
        </div>
      )}
      {error && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );
}

// --- what the customer has to register -------------------------------------

function RedirectCard({ config }: { config: AuthConfig }) {
  return (
    <Card className="p-4">
      <h3 className="mb-1 text-sm font-medium">Beim Anbieter eintragen</h3>
      <p className="mb-3 text-xs muted">
        Der Kunde legt in seinem Anbieter eine Anwendung an — Authorization Code mit PKCE, Typ
        „Web“ — und trägt dort diese Rückkehradresse ein. Die Client-ID daraus kommt oben ins
        Formular, das Secret (falls eines vergeben wird) zum Betrieb in die Umgebung des Servers.
      </p>
      {config.redirectUri ? (
        <CopyLine label="Redirect-URI" value={config.redirectUri} />
      ) : (
        <Notice tone="warning">
          Es ist keine Portal-Adresse konfiguriert (MBM_PORTAL_BASE_URL); ohne sie gibt es keine
          Rückkehradresse und keine externe Anmeldung.
        </Notice>
      )}
    </Card>
  );
}

function CopyLine({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Ohne Zwischenablage bleibt der Wert zum Markieren stehen.
    }
  }
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium muted">{label}</span>
      <div className="flex items-center gap-2">
        <Input value={value} readOnly onFocus={(e) => e.currentTarget.select()} className="font-mono" />
        <Button size="sm" onClick={copy} title="In die Zwischenablage">
          {copied ? <Check className="size-3.5" aria-hidden /> : <Copy className="size-3.5" aria-hidden />}
          {copied ? "Kopiert" : "Kopieren"}
        </Button>
      </div>
    </div>
  );
}

// --- claimed mail domains ---------------------------------------------------

function DomainsCard({
  companyId,
  config,
  onChanged,
}: {
  companyId: number;
  config: AuthConfig;
  onChanged: () => void;
}) {
  const [domain, setDomain] = useState("");
  const [releasing, setReleasing] = useState<string | null>(null);

  const claim = useMutation({
    mutationFn: (d: string) =>
      api<void>(`/api/admin/companies/${companyId}/auth/domains`, {
        method: "POST",
        body: { domain: d },
      }),
    onSuccess: () => {
      setDomain("");
      onChanged();
    },
  });
  const release = useMutation({
    mutationFn: (d: string) =>
      api<void>(`/api/admin/companies/${companyId}/auth/domains/${encodeURIComponent(d)}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      setReleasing(null);
      onChanged();
    },
  });

  return (
    <Card className="p-4">
      <h3 className="mb-1 text-sm font-medium">Mail-Domänen der Firma</h3>
      <p className="mb-3 text-xs muted">
        Eine bekannte Adresse findet ihre Firma über das Benutzerkonto. Die Domäne braucht es für
        die anderen: Wer sich mit einer Adresse dieser Domäne anmeldet und noch kein Konto hat,
        wird dieser Firma zugeordnet — beim Identitätsanbieter also dorthin geschickt. Jede
        Domäne kann nur einer Firma gehören; öffentliche Anbieter wie gmail.com gehen nicht.
      </p>

      <form
        className="mb-3 flex items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (domain.trim()) claim.mutate(domain.trim());
        }}
      >
        <Input
          value={domain}
          placeholder="kunde.at"
          onChange={(e) => setDomain(e.target.value)}
          spellCheck={false}
          className="sm:max-w-72"
        />
        <Button type="submit" disabled={!domain.trim() || claim.isPending}>
          <Plus className="size-4" aria-hidden /> Beanspruchen
        </Button>
      </form>
      {claim.error && (
        <div className="mb-3">
          <ErrorNote>{(claim.error as Error).message}</ErrorNote>
        </div>
      )}

      {config.domains.length === 0 ? (
        <Empty
          title="Noch keine Domäne"
          hint={
            config.mode === "oidc"
              ? "Ohne Domäne kommen nur Personen zum Anbieter, die hier schon ein Konto haben."
              : "Bei Passwort-Anmeldung ist das nur nötig, wenn Unbekannte dieser Firma zugeordnet werden sollen."
          }
        />
      ) : (
        <Table dense>
          <thead>
            <tr>
              <Th>Domäne</Th>
              <Th className="w-10" />
            </tr>
          </thead>
          <tbody>
            {config.domains.map((d) => (
              <tr key={d}>
                <Td className="font-mono">{d}</Td>
                <Td>
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`${d} freigeben`}
                    title="Freigeben"
                    onClick={() => setReleasing(d)}
                  >
                    <Trash2 className="size-3.5" aria-hidden />
                  </Button>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <ConfirmDialog
        open={releasing !== null}
        title="Domäne freigeben"
        message={
          <p>
            <strong>{releasing}</strong> gehört danach keiner Firma mehr. Wer sich mit einer
            solchen Adresse ohne Konto anmeldet, wird nicht mehr zugeordnet.
          </p>
        }
        confirmLabel="Freigeben"
        error={release.error ? (release.error as Error).message : null}
        onConfirm={() => releasing && release.mutate(releasing)}
        onClose={() => {
          setReleasing(null);
          release.reset();
        }}
        busy={release.isPending}
      />
    </Card>
  );
}

// --- invitations -------------------------------------------------------------

function InviteCard({
  companyId,
  config,
  onSent,
}: {
  companyId: number;
  config: AuthConfig;
  onSent: () => void;
}) {
  const invite = useMutation({
    mutationFn: () =>
      api<void>(`/api/admin/companies/${companyId}/invite-all`, { method: "POST" }),
    onSuccess: onSent,
  });
  const n = config.passwordlessCount;

  return (
    <Card className="p-4">
      <h3 className="mb-1 text-sm font-medium">Einladungen</h3>
      <p className="mb-3 text-xs muted">
        {n === 0
          ? "Alle aktiven Personen dieser Firma haben ein Passwort."
          : `${n} ${n === 1 ? "aktive Person hat" : "aktive Personen haben"} kein Passwort.`}{" "}
        {config.mode === "oidc"
          ? "Bei Anmeldung über den Anbieter brauchen sie keines; eine Einladung ist erst bei der Umstellung auf Passwörter sinnvoll."
          : "Eine Einladung schickt jeder von ihnen einen Link, mit dem sie ihr erstes Passwort setzen; er ist zwei Wochen gültig."}
      </p>
      {invite.error && (
        <div className="mb-3">
          <ErrorNote>{(invite.error as Error).message}</ErrorNote>
        </div>
      )}
      {invite.isSuccess && (
        <div className="mb-3">
          <Notice tone="success">Die Einladungen sind unterwegs.</Notice>
        </div>
      )}
      <Toolbar>
        <Button onClick={() => invite.mutate()} disabled={n === 0 || invite.isPending}>
          {invite.isPending ? "Sendet…" : "Alle ohne Passwort einladen"}
        </Button>
      </Toolbar>
    </Card>
  );
}
